import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Stitch from './Stitch';
import { showToast } from '../ui/Toast';
import '../../styles/shopper.css';

export interface Shortcut {
  id: string;
  /** What the bar prints, one key cap each (e.g. ['←', '→']). */
  caps: string[];
  label: string;
  /** KeyboardEvent.key values that trigger it (letters in lower case). */
  keys: string[];
  onPress: (key: string) => void;
  /** The page's main action: its key cap is ringed in orange thread. */
  primary?: boolean;
  /** Handled but not shown (e.g. ↑ ↓ scrolling the details). */
  silent?: boolean;
  /** Only take the key when this says so (e.g. Enter while a card has focus); otherwise the browser keeps it. */
  when?: (e: KeyboardEvent) => boolean;
}

interface ShortcutBarProps {
  shortcuts: Shortcut[];
  /** True while a sheet or dialog is open: keys go to it, not to the page. */
  disabled?: boolean;
  label?: string;
}

const HIDDEN_KEY = 'basiqr-shortcuts-hidden';
const readHidden = () => {
  try {
    return localStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
};
const writeHidden = (hidden: boolean) => {
  try {
    if (hidden) localStorage.setItem(HIDDEN_KEY, '1');
    else localStorage.removeItem(HIDDEN_KEY);
  } catch {
    // private mode: the choice lasts for this visit
  }
};

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

/**
 * The shopper pages' shortcut bar (#467): a woven label sewn on at the bottom
 * left, listing only the keys that work on this page. Pressing a key rings its
 * cap in orange thread; clicking an entry does the same as its key. × hides
 * it and ? brings it back. Render it on screens with a keyboard only.
 */
const ShortcutBar: React.FC<ShortcutBarProps> = ({ shortcuts, disabled = false, label = 'Keyboard shortcuts' }) => {
  const [hidden, setHidden] = useState(readHidden);
  const [hit, setHit] = useState<string | null>(null);
  const hitTimer = useRef<number>();
  const latest = useRef(shortcuts);
  latest.current = shortcuts;

  const flash = useCallback((id: string) => {
    setHit(id);
    window.clearTimeout(hitTimer.current);
    hitTimer.current = window.setTimeout(() => setHit(null), 380);
  }, []);

  useEffect(() => () => window.clearTimeout(hitTimer.current), []);

  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key === '?') {
        e.preventDefault();
        setHidden(false);
        writeHidden(false);
        return;
      }
      const s = latest.current.find(x => x.keys.includes(key));
      if (!s || (s.when && !s.when(e))) return;
      e.preventDefault();
      if (!s.silent) flash(s.id);
      s.onPress(key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, flash]);

  const hide = () => {
    setHidden(true);
    writeHidden(true);
    showToast.custom('Shortcuts hidden. Press ? to bring them back.');
  };

  const shown = shortcuts.filter(s => !s.silent);
  if (shown.length === 0) return null;

  return (
    <nav className={`sb${hidden ? ' is-hidden' : ''}`} aria-label={label} aria-hidden={hidden}>
      <Stitch sewOn={hidden ? false : 'mount'} sewKey={hidden ? 'h' : 'v'} />
      {shown.map(s => (
        <button
          key={s.id}
          type="button"
          tabIndex={hidden ? -1 : 0}
          className={`sb-entry${s.primary ? ' is-primary' : ''}${hit === s.id ? ' is-hit' : ''}`}
          onClick={() => {
            flash(s.id);
            s.onPress(s.keys[0]);
          }}
        >
          <span className="sb-caps">
            {s.caps.map(c => (
              <kbd key={c}>{c}</kbd>
            ))}
          </span>
          <span>{s.label}</span>
        </button>
      ))}
      <button type="button" className="sb-hide" onClick={hide} tabIndex={hidden ? -1 : 0} aria-label="Hide the shortcuts">
        <X size={13} aria-hidden="true" />
      </button>
    </nav>
  );
};

export default ShortcutBar;
